import { Phone, Send, Calculator } from "lucide-react";
import { trackEvent } from "@/lib/analytics";

interface MobileStickyCTAProps {
  /** Optional: open lead form callback. If omitted, button scrolls to top. */
  onCalc?: () => void;
  lang?: "ru" | "uz";
}

const labels = {
  ru: { call: "Позвонить", tg: "Telegram", calc: "Замер за 0 сум" },
  uz: { call: "Qo‘ng‘iroq", tg: "Telegram", calc: "Bepul o‘lchov" },
};

/**
 * Mobile-first sticky bottom action bar with 3 CTAs.
 * Replaces the old StickyTelegram FAB. Hidden on desktop (md+).
 */
const MobileStickyCTA = ({ onCalc, lang = "ru" }: MobileStickyCTAProps) => {
  const t = labels[lang];

  const handleCalc = () => {
    trackEvent("open_lead_form", { lead_type: "sticky_cta_calc" });
    if (onCalc) onCalc();
    else {
      const ev = new CustomEvent("zebra:open-lead", { detail: { source: "sticky_cta_calc" } });
      window.dispatchEvent(ev);
    }
  };

  return (
    <div
      className="fixed bottom-0 inset-x-0 z-40 md:hidden border-t border-border bg-background/95 backdrop-blur-md shadow-[0_-6px_20px_rgba(0,0,0,0.08)]"
      style={{ paddingBottom: "env(safe-area-inset-bottom, 0)" }}
      role="navigation"
      aria-label={lang === "uz" ? "Tezkor amallar" : "Быстрые действия"}
    >
      <div className="grid grid-cols-3 gap-1 p-2">
        <a
          href="tel:+998781480001"
          onClick={() => trackEvent("click_call", { source: "sticky_cta" })}
          className="flex flex-col items-center justify-center gap-1 py-2 rounded-xl text-foreground active:bg-muted transition-colors"
          aria-label={t.call}
        >
          <Phone className="h-5 w-5 text-primary" />
          <span className="text-[11px] font-semibold">{t.call}</span>
        </a>
        <a
          href="https://t.me/zebrajaluzi"
          target="_blank"
          rel="noopener"
          onClick={() => trackEvent("click_telegram_cta", { source: "sticky_cta" })}
          className="flex flex-col items-center justify-center gap-1 py-2 rounded-xl text-foreground active:bg-muted transition-colors"
          aria-label={t.tg}
        >
          <Send className="h-5 w-5 text-primary" />
          <span className="text-[11px] font-semibold">{t.tg}</span>
        </a>
        <button
          onClick={handleCalc}
          className="relative flex flex-col items-center justify-center gap-1 py-2 rounded-xl bg-primary text-primary-foreground active:scale-95 transition-transform shadow-lg shadow-primary/30 overflow-hidden"
          aria-label={t.calc}
        >
          <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-primary-foreground/25 to-transparent animate-[shimmer_2.4s_infinite]" aria-hidden />
          <Calculator className="h-5 w-5 relative" />
          <span className="text-[11px] font-bold relative whitespace-nowrap">{t.calc}</span>
        </button>
      </div>
    </div>
  );
};

export default MobileStickyCTA;
